import { COLOR_NAMES } from './logging';

/**
 * A mission's name — `green-triangle` — as readable text.
 *
 * The two halves are separate keys because word order is not universal: Greek
 * puts the shape first ("τρίγωνο πράσινο"), so the joining is itself a
 * translatable string. Sixteen joined keys per language would say the same
 * thing at eight times the cost, and would need re-doing the day a fifth
 * colour arrives.
 *
 * A mission whose colour is `any` is the wildcard and has no colour half.
 *
 * @param {?{name: string, color: string}} mission
 * @param {function(string, object=): string} t The active locale's translator.
 *
 * @return {string} Empty for a missing mission — a caller mid-round between
 *   missions has nothing to name, and that is not an error.
 */
export function missionLabel(mission, t) {
  if (!mission?.name) return '';
  if (mission.color === 'any') return t('game.anyRobot');

  const [color, shape] = mission.name.split('-');
  return t('game.missionLabel', {
    color: t(`game.color.${color}`),
    shape: t(`game.shape.${shape}`),
  });
}

/**
 * A robot's colour — `#e0a325` — as a word in the reader's language.
 *
 * The hex→word map is the one `logging.js` already keeps (and which mirrors
 * the server's `COLOR_NAMES`), so this is a join and not a fifth copy of the
 * palette. An unknown colour is empty rather than a raw hex: a hint that says
 * "#123456" is worse than a hint that says nothing.
 *
 * @param {?string} color A robot colour, as a hex string.
 * @param {function(string, object=): string} t The active locale's translator.
 *
 * @return {string}
 */
export function robotColorName(color, t) {
  const name = COLOR_NAMES[color];
  return name ? t(`game.color.${name}`) : '';
}
